import { ArrowRight, Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useActiveFrame, useCourses, useMe, useModules } from '../api/hooks.js';
import { Loading, Page, PageHeader } from '../components/Page.js';
import { Badge, Button, Meter, Panel, Stat, cx } from '../components/ui.js';
import s from './HangarPage.module.css';

const LOOP = ['Learn', 'Write', 'Validate', 'Compile', 'Install', 'Battle', 'Upgrade'];

export function HangarPage() {
  const { data: me } = useMe();
  const courses = useCourses();
  const frame = useActiveFrame();
  const modules = useModules();
  if (!me || courses.isLoading || !frame) return <Loading />;
  const p = me.player;
  const lessons = courses.data?.flatMap((c) => c.lessons) ?? [];
  const nextLesson = lessons.find((l) => l.status === 'in_progress' || l.status === 'available');
  const workshop = p.features.find((f) => f.id === 'workshop')!;
  const playerModules = modules.data?.filter((m) => m.origin === 'player') ?? [];
  const installedPlayerCode = frame.software.some((sl) => sl.module?.origin === 'player');

  let next: { stage: number; title: string; body: string; cta: ReactNode };
  if (!workshop.unlocked) {
    next = {
      stage: 0,
      title: nextLesson ? `Continue: ${nextLesson.title}` : 'Academy',
      body: `Your Frame runs factory firmware that can only shoot. Learn enough Python to write your own software — the Workshop unlocks at level ${workshop.minLevel}.`,
      cta: nextLesson && (
        <Link to={`/academy/${nextLesson.id}`}>
          <Button variant="primary" size="large" icon={<ArrowRight size={16} />}>
            {nextLesson.status === 'in_progress' ? 'Resume lesson' : 'Start lesson'}
          </Button>
        </Link>
      ),
    };
  } else if (playerModules.length === 0) {
    next = {
      stage: 1,
      title: 'Write your first routine',
      body: 'The Workshop is online. Take the Emergency Repair Protocol contract: write the routine, validate it, and compile it into a module.',
      cta: (
        <Link to="/workshop?contract=emergency-repair-protocol">
          <Button variant="primary" size="large" icon={<ArrowRight size={16} />}>
            Open the Workshop
          </Button>
        </Link>
      ),
    };
  } else if (!installedPlayerCode) {
    next = {
      stage: 4,
      title: 'Install your module',
      body: 'Compiled code does nothing in the library. Install it into a free processor thread on your Frame.',
      cta: (
        <Link to="/frame">
          <Button variant="primary" size="large" icon={<ArrowRight size={16} />}>
            Frame bay
          </Button>
        </Link>
      ),
    };
  } else {
    next = {
      stage: 5,
      title: 'Deploy to the Arena',
      body: 'Your software is installed. Send the Frame into a training battle and watch your code take over.',
      cta: (
        <Link to="/arena">
          <Button variant="primary" size="large" icon={<ArrowRight size={16} />}>
            Enter the Arena
          </Button>
        </Link>
      ),
    };
  }

  return (
    <Page>
      <PageHeader eyebrow="Hangar" title={`Pilot ${me.user.username}`} description="Command overview." />
      <div className={s.hero}>
        <div className={s.next}>
          <div className={s.loop}>
            {LOOP.map((step, i) => (
              <span key={step} className={cx(i <= next.stage && s.loopActive)}>
                {step}
                {i < LOOP.length - 1 && ' →'}
              </span>
            ))}
          </div>
          <h2>{next.title}</h2>
          <p>{next.body}</p>
          <div>{next.cta}</div>
        </div>
        <Panel label="Active Frame" title={frame.name}>
          <div className={s.list}>
            <Meter label="Processor allocation" value={frame.cpuAllocated} max={frame.stats.cyclesPerTick} display={`${frame.cpuAllocated}/${frame.stats.cyclesPerTick} cyc`} />
            {frame.software.map((sl) => (
              <div key={sl.slotIndex} className={s.feature}>
                <span className="label">Thread {sl.slotIndex}</span>
                <span>{sl.module ? sl.module.name : <span className="muted">Empty</span>}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
      <div className={s.grid}>
        <Panel label="Progression">
          <div className={s.list}>
            <Stat label="Player level" value={p.level.level} />
            <Meter label="Programming" value={p.programming.xp - p.programming.levelStartXp} max={(p.programming.nextLevelXp ?? p.programming.xp + 1) - p.programming.levelStartXp} display={`LV ${p.programming.level}`} />
            {p.languages.map((l) => (
              <Meter key={l.language} label={`${l.displayName} mastery`} value={l.percent} max={100} display={`${l.percent}%`} color="var(--cyber)" />
            ))}
          </div>
        </Panel>
        <Panel label="Record">
          <div className={s.list}>
            <Stat label="Lessons completed" value={p.stats.lessonsCompleted} />
            <Stat label="Modules compiled" value={p.stats.modulesCompiled} />
            <Stat label="Battles won" value={`${p.stats.battlesWon}/${p.stats.battlesFought}`} />
          </div>
        </Panel>
        <Panel label="Systems">
          {p.features.map((f) => (
            <div key={f.id} className={s.feature}>
              <span>{f.name}</span>
              {f.unlocked ? <Badge tone="ok">Online</Badge> : <Badge icon={<Lock size={10} />}>{f.implemented ? `LV ${f.minLevel}` : 'Coming soon'}</Badge>}
            </div>
          ))}
        </Panel>
      </div>
    </Page>
  );
}
